(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.AstalieBonus = api;
  if (root && root.document) api.mount(root.document);
})(typeof window !== "undefined" ? window : globalThis, function () {
  const ROLE_BONUS = "bonus";
  const CART_SECTIONS = [
    "cart-drawer",
    "cart-icon-bubble",
    "main-cart-items",
    "main-cart-footer",
  ];
  let mutationRunning = false;
  let cartStateUnconfirmed = false;
  let scheduled = 0;
  let cartMutationTail = Promise.resolve();
  let pendingCartMutations = 0;

  function runCartMutation(operation) {
    pendingCartMutations += 1;
    if (typeof document !== "undefined") updateCheckoutPending(document);
    const result = cartMutationTail.then(operation);
    cartMutationTail = result.catch(() => {});
    return result.finally(() => {
      pendingCartMutations -= 1;
      if (typeof document !== "undefined") updateCheckoutPending(document);
    });
  }

  function text(value) {
    return typeof value === "string" ? value.trim() : "";
  }

  function positiveInteger(value) {
    const number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : 0;
  }

  function variantNumericId(value) {
    return text(value).split("/").pop() || "";
  }

  function normalizeConfig(value) {
    if (!value || typeof value !== "object" || Array.isArray(value))
      return null;
    const campaignId = text(value.campaignId);
    const tier = positiveInteger(value.tier);
    const previousThresholdMinor = Number(value.previousThresholdMinor);
    const thresholdMinor = Number(value.thresholdMinor);
    const version = Number(value.version) === 2 ? 2 : 1;
    const eligibleProducts = Array.isArray(value.eligibleProducts)
      ? value.eligibleProducts.filter((product) =>
          text(product && product.productId),
        )
      : [];
    if (
      !campaignId ||
      !tier ||
      !Number.isSafeInteger(previousThresholdMinor) ||
      previousThresholdMinor < 0 ||
      !Number.isSafeInteger(thresholdMinor) ||
      thresholdMinor <= previousThresholdMinor ||
      (version === 2 &&
        (text(value.currency) !== "JPY" ||
          value.pricingBasis !== "line_final_total"))
    ) {
      return null;
    }
    return {
      campaignId,
      eligibleProducts,
      grantMode: value.grantMode === "choice" ? "choice" : "automatic",
      previousThresholdMinor,
      thresholdMinor,
      tier,
      version,
    };
  }

  function normalizeMembership(value) {
    const config = normalizeConfig(value);
    const contributionMinor = Number(value && value.contributionMinor);
    const giftProductId = text(value && value.giftProductId);
    const giftVariantId = text(value && value.giftVariantId);
    if (
      !config ||
      (config.version === 1 &&
        (!Number.isSafeInteger(contributionMinor) || contributionMinor <= 0)) ||
      !giftProductId ||
      !giftVariantId
    ) {
      return null;
    }
    return {
      ...config,
      contributionMinor,
      giftHandle: text(value.giftHandle),
      giftProductId,
      giftTitle: text(value.giftTitle),
      giftVariantId,
    };
  }

  function stageKey(campaignId, tier) {
    return `${campaignId}::${tier}`;
  }

  function truncateStagePercent(
    totalMinor,
    previousThresholdMinor,
    thresholdMinor,
  ) {
    const stageMinor = thresholdMinor - previousThresholdMinor;
    if (stageMinor <= 0) return 0;
    const earnedMinor = Math.max(
      0,
      Math.min(stageMinor, totalMinor - previousThresholdMinor),
    );
    return Math.floor((earnedMinor * 1000) / stageMinor) / 10;
  }

  function formatJpyMinor(value) {
    const minor = Number(value);
    const yen = Number.isFinite(minor)
      ? Math.max(0, Math.ceil(minor / 100))
      : 0;
    return `¥${new Intl.NumberFormat("ja-JP").format(yen)}`;
  }

  function conditionText(gift) {
    if (gift.version !== 2) return "";
    const reward = gift.grantMode === "choice" ? "特典から1点選べます" : "特典を1点プレゼント";
    return `対象商品を税込${formatJpyMinor(gift.thresholdMinor)}以上ご購入で${reward}（1回のご注文につき1点）`;
  }

  function normalizeCatalog(data) {
    const catalog = new Map();
    (Array.isArray(data && data.giftProducts) ? data.giftProducts : []).forEach(
      (candidate) => {
        const handle = text(candidate && candidate.handle);
        const variantId = text(candidate && candidate.variantId);
        if (!handle || !variantId) return;
        const images = (Array.isArray(candidate.images) ? candidate.images : [])
          .map((image) => ({
            alt: text(image && image.alt),
            height: positiveInteger(image && image.height),
            src: text(image && image.src),
            width: positiveInteger(image && image.width),
          }))
          .filter((image) => image.src);
        const product = {
          available: candidate.available === true,
          handle,
          images,
          title: text(candidate.title),
          variantId,
        };
        const current = catalog.get(variantId);
        if (!current || images.length >= current.images.length)
          catalog.set(variantId, product);
      },
    );
    return catalog;
  }

  function buildCampaignState(data) {
    const items = Array.isArray(data && data.items) ? data.items : [];
    const catalog = normalizeCatalog(data);
    const stages = new Map();

    function ensureStage(rule) {
      const key = stageKey(rule.campaignId, rule.tier);
      if (!stages.has(key)) {
        stages.set(key, {
          campaignId: rule.campaignId,
          gifts: new Map(),
          previousThresholdMinor: rule.previousThresholdMinor,
          thresholdMinor: rule.thresholdMinor,
          tier: rule.tier,
          totalMinor: 0,
        });
      }
      return stages.get(key);
    }

    function upsertGift(stage, gift, contributionMinor) {
      const current = stage.gifts.get(gift.giftProductId);
      stage.gifts.set(gift.giftProductId, {
        ...current,
        ...gift,
        totalMinor: (current?.totalMinor || 0) + contributionMinor,
      });
    }

    items.forEach((item) => {
      if (item.role === ROLE_BONUS) {
        const config = normalizeConfig(item.config);
        if (!config) return;
        const stage = ensureStage(config);
        upsertGift(
          stage,
          {
            ...config,
            giftHandle: text(item.handle),
            giftProductId: text(item.productId),
            giftTitle: text(item.title),
            giftVariantId: text(item.variantId),
          },
          0,
        );
        return;
      }

      const contributions = new Map();
      const lineTotalMinor = positiveInteger(item.lineTotalMinor);
      (Array.isArray(item.memberships) ? item.memberships : []).forEach(
        (candidate) => {
          const membership = normalizeMembership(candidate);
          if (!membership) return;
          const contributionMinor =
            membership.version === 2
              ? lineTotalMinor
              : membership.contributionMinor * positiveInteger(item.quantity);
          if (!contributionMinor) return;
          const stage = ensureStage(membership);
          upsertGift(stage, membership, contributionMinor);
          const key = stageKey(membership.campaignId, membership.tier);
          const current = contributions.get(key);
          contributions.set(
            key,
            current ? Math.min(current, contributionMinor) : contributionMinor,
          );
        },
      );
      contributions.forEach((contributionMinor, key) => {
        const stage = stages.get(key);
        if (stage) stage.totalMinor += contributionMinor;
      });
    });

    const campaigns = new Map();
    Array.from(stages.values())
      .sort(
        (left, right) =>
          left.campaignId.localeCompare(right.campaignId) ||
          left.tier - right.tier,
      )
      .forEach((stage) => {
        stage.gifts = Array.from(stage.gifts.values()).map((gift) => {
          const product = catalog.get(gift.giftVariantId);
          return {
            ...gift,
            eligible: gift.totalMinor >= gift.thresholdMinor,
            giftAvailable: product?.available === true,
            giftImages: product?.images || [],
            giftTitle: product?.title || gift.giftTitle,
            percent: truncateStagePercent(
              gift.totalMinor,
              gift.previousThresholdMinor,
              gift.thresholdMinor,
            ),
          };
        });
        stage.eligible = stage.gifts.some((gift) => gift.eligible);
        stage.percent = Math.max(0, ...stage.gifts.map((gift) => gift.percent));
        stage.remainingMinor = Math.min(
          ...stage.gifts.map((gift) =>
            Math.max(0, gift.thresholdMinor - gift.totalMinor),
          ),
        );
        if (!campaigns.has(stage.campaignId))
          campaigns.set(stage.campaignId, []);
        campaigns.get(stage.campaignId).push(stage);
      });
    return Array.from(campaigns, ([campaignId, campaignStages]) => ({
      campaignId,
      stages: campaignStages,
    }));
  }

  function eligibleChoiceStages(campaign) {
    return (
      campaign && Array.isArray(campaign.stages) ? campaign.stages : []
    ).filter(
      (stage) =>
        stage.eligible &&
        stage.gifts.some(
          (gift) => gift.grantMode === "choice" && gift.eligible,
        ),
    );
  }

  function selectedChoiceForStage(data, stage) {
    const items = Array.isArray(data && data.items) ? data.items : [];
    for (const item of items) {
      if (item.role !== ROLE_BONUS) continue;
      const config = normalizeConfig(item.config);
      if (
        !config ||
        config.grantMode !== "choice" ||
        config.campaignId !== stage.campaignId ||
        config.tier !== stage.tier
      ) {
        continue;
      }
      const gift = stage.gifts.find(
        (candidate) =>
          candidate.grantMode === "choice" &&
          candidate.eligible &&
          candidate.giftProductId === text(item.productId) &&
          candidate.giftVariantId === text(item.variantId),
      );
      if (gift) return { gift, item };
    }
    return null;
  }

  function pendingChoiceStages(data, campaigns) {
    return campaigns.flatMap((campaign) =>
      eligibleChoiceStages(campaign).filter(
        (stage) => !selectedChoiceForStage(data, stage),
      ),
    );
  }

  function readCartData(documentRef) {
    const nodes = Array.from(
      documentRef.querySelectorAll("[data-astalie-bonus-cart]"),
    );
    const node =
      documentRef.querySelector(
        "cart-drawer.active [data-astalie-bonus-cart]",
      ) ||
      documentRef.querySelector("#main-cart-items [data-astalie-bonus-cart]") ||
      documentRef.querySelector(
        "#CartDrawer-CartItems [data-astalie-bonus-cart]",
      ) ||
      nodes[0];
    if (!node) return { giftProducts: [], items: [] };
    try {
      return JSON.parse(node.textContent || "{}");
    } catch (_error) {
      return { giftProducts: [], items: [] };
    }
  }

  function createElement(documentRef, tag, className, value) {
    const element = documentRef.createElement(tag);
    if (className) element.className = className;
    if (value !== undefined && value !== null) element.textContent = value;
    return element;
  }

  function appendGiftImage(documentRef, host, gift, className) {
    const image = gift.giftImages?.[0];
    if (!image) {
      host.append(
        createElement(
          documentRef,
          "span",
          `${className} astalie-bonus__image-empty`,
          "画像なし",
        ),
      );
      return;
    }
    const element = createElement(documentRef, "img", className);
    element.src = image.src;
    element.alt = image.alt || gift.giftTitle || "特典";
    element.loading = "lazy";
    if (image.width) element.width = image.width;
    if (image.height) element.height = image.height;
    host.append(element);
  }

  function renderChoiceStage(documentRef, data, choiceStage) {
    const selection = selectedChoiceForStage(data, choiceStage);
    const candidates = choiceStage.gifts.filter(
      (gift) => gift.grantMode === "choice" && gift.eligible,
    );
    const unavailable =
      candidates.length > 0 &&
      candidates.every((gift) => gift.giftAvailable === false);
    const panel = createElement(
      documentRef,
      "div",
      `astalie-bonus__choice-stage${selection ? " is-selected" : " is-pending"}`,
    );
    const head = createElement(
      documentRef,
      "div",
      "astalie-bonus__choice-head",
    );
    head.append(
      createElement(
        documentRef,
        "strong",
        "astalie-bonus__choice-tier",
        `特典 ${choiceStage.tier}`,
      ),
      createElement(
        documentRef,
        "span",
        "astalie-bonus__choice-status",
        selection ? "選択済み" : unavailable ? "在庫不足" : "選択が必要",
      ),
    );

    const button = createElement(
      documentRef,
      "button",
      "astalie-bonus__choice-open",
    );
    button.type = "button";
    button.dataset.astalieBonusOpen = "true";
    button.dataset.campaignId = choiceStage.campaignId;
    button.dataset.tier = String(choiceStage.tier);
    button.setAttribute(
      "aria-label",
      selection
        ? `特典 ${choiceStage.tier}を変更`
        : `特典 ${choiceStage.tier}を選ぶ`,
    );

    const media = createElement(
      documentRef,
      "span",
      "astalie-bonus__choice-preview",
    );
    if (selection) {
      appendGiftImage(
        documentRef,
        media,
        selection.gift,
        "astalie-bonus__choice-image",
      );
    } else {
      candidates.slice(0, 3).forEach((gift) =>
        appendGiftImage(
          documentRef,
          media,
          gift,
          "astalie-bonus__choice-image",
        ),
      );
    }

    const copy = createElement(
      documentRef,
      "span",
      "astalie-bonus__choice-copy",
    );
    copy.append(
      createElement(
        documentRef,
        "span",
        "astalie-bonus__choice-title",
        selection?.gift.giftTitle || (unavailable ? "在庫不足" : "選べます"),
      ),
      createElement(
        documentRef,
        "span",
        "astalie-bonus__choice-count",
        selection ? "" : `${candidates.length}種類`,
      ),
    );
    button.append(
      media,
      copy,
      createElement(
        documentRef,
        "span",
        "astalie-bonus__choice-action",
        selection ? "変更" : unavailable ? "在庫不足" : "選ぶ",
      ),
    );
    panel.append(head, button);
    return panel;
  }

  function setCheckoutPending(documentRef, count) {
    documentRef
      .querySelectorAll('button[name="checkout"], input[name="checkout"]')
      .forEach((button) => {
        if (count > 0) {
          if (!button.disabled) button.dataset.astalieBonusBlocked = "true";
          button.disabled = true;
          button.setAttribute("aria-disabled", "true");
        } else if (button.dataset.astalieBonusBlocked === "true") {
          button.disabled = false;
          button.removeAttribute("aria-disabled");
          delete button.dataset.astalieBonusBlocked;
        }
      });
    documentRef.querySelectorAll(".cart__dynamic-checkout-buttons").forEach((container) => {
      if (count > 0) {
        if (!container.inert) container.dataset.astalieBonusBlocked = "true";
        container.inert = true;
      } else if (container.dataset.astalieBonusBlocked === "true") {
        container.inert = false;
        delete container.dataset.astalieBonusBlocked;
      }
    });
  }

  function checkoutPendingCount(data, campaigns) {
    const plan = desiredCartChanges(data, campaigns);
    return pendingChoiceStages(data, campaigns).length +
      plan.additions.length + plan.changes.length;
  }

  function updateCheckoutPending(documentRef) {
    const data = readCartData(documentRef);
    const pending = checkoutPendingCount(data, buildCampaignState(data));
    setCheckoutPending(documentRef, pendingCartMutations || mutationRunning || cartStateUnconfirmed || pending);
    return pendingCartMutations || mutationRunning || cartStateUnconfirmed || pending;
  }

  function renderProgress(documentRef, data, campaigns) {
    const pendingCount = checkoutPendingCount(data, campaigns);
    documentRef
      .querySelectorAll("[data-astalie-bonus-progress]")
      .forEach((container) => {
        const host = container.querySelector("[data-astalie-bonus-campaigns]");
        const summary = container.querySelector("[data-astalie-bonus-summary]");
        if (!host || !summary) return;
        host.replaceChildren();
        container.hidden = campaigns.length === 0;
        container.dataset.astalieBonusPending = String(pendingCount);
        if (!campaigns.length) return;

        let earnedCount = 0;
        campaigns.forEach((campaign) => {
          earnedCount += campaign.stages.filter(
            (stage) => stage.eligible,
          ).length;
          const activeStage =
            campaign.stages.find((stage) => !stage.eligible) ||
            campaign.stages[campaign.stages.length - 1];
          const campaignNode = createElement(
            documentRef,
            "div",
            "astalie-bonus__campaign",
          );
          const head = createElement(
            documentRef,
            "div",
            "astalie-bonus__stage-head",
          );
          const label = createElement(
            documentRef,
            "span",
            "astalie-bonus__stage-label",
            `特典 ${activeStage.tier}`,
          );
          const status = createElement(
            documentRef,
            "strong",
            "astalie-bonus__stage-status",
            activeStage.eligible
              ? "達成"
              : `あと ${formatJpyMinor(activeStage.remainingMinor)}`,
          );
          const track = createElement(
            documentRef,
            "div",
            "astalie-bonus__track",
          );
          const fill = createElement(
            documentRef,
            "span",
            "astalie-bonus__fill",
          );
          fill.style.width = `${activeStage.percent}%`;
          track.append(fill);
          head.append(label, status);
          campaignNode.append(head, track);
          new Set(activeStage.gifts.map(conditionText).filter(Boolean)).forEach((condition) => {
            campaignNode.append(createElement(documentRef, "p", "astalie-bonus__condition", condition));
          });
          eligibleChoiceStages(campaign).forEach((choiceStage) =>
            campaignNode.append(
              renderChoiceStage(documentRef, data, choiceStage),
            ),
          );
          host.append(campaignNode);
        });
        summary.textContent = pendingCount ? "特典の追加待ち" : earnedCount ? `${earnedCount}点獲得` : "";
      });
    setCheckoutPending(documentRef, mutationRunning || cartStateUnconfirmed || pendingCount);
  }

  function desiredCartChanges(data, campaigns) {
    const items = Array.isArray(data && data.items) ? data.items : [];
    const stageMap = new Map();
    campaigns.forEach((campaign) =>
      campaign.stages.forEach((stage) =>
        stageMap.set(stageKey(stage.campaignId, stage.tier), stage),
      ),
    );
    const additions = [];
    const changes = [];
    const desiredAutomaticByGroup = new Map();

    campaigns.forEach((campaign) => {
      campaign.stages.forEach((stage) => {
        if (!stage.eligible) return;
        const gift = stage.gifts
          .filter((candidate) =>
            candidate.grantMode === "automatic" && candidate.eligible,
          )
          .sort((left, right) =>
            left.giftProductId.localeCompare(right.giftProductId),
          )[0];
        if (gift)
          desiredAutomaticByGroup.set(
            stageKey(stage.campaignId, stage.tier),
            gift,
          );
      });
    });

    const occupiedGroups = new Set();
    const presentAutomaticGroups = new Set();
    items.forEach((item) => {
      if (item.role !== ROLE_BONUS) return;
      const config = normalizeConfig(item.config);
      const group = config ? stageKey(config.campaignId, config.tier) : "";
      const stage = config ? stageMap.get(group) : null;
      const gift = stage?.gifts.find(
        (candidate) =>
          candidate.giftProductId === text(item.productId) &&
          candidate.giftVariantId === text(item.variantId),
      );
      if (!config || !stage || !gift?.eligible) {
        changes.push({ key: item.key, quantity: 0 });
        return;
      }
      if (occupiedGroups.has(group)) {
        changes.push({ key: item.key, quantity: 0 });
        return;
      }
      if (config.grantMode === "automatic") {
        const desiredGift = desiredAutomaticByGroup.get(group);
        if (
          !desiredGift ||
          desiredGift.giftVariantId !== text(item.variantId)
        ) {
          changes.push({ key: item.key, quantity: 0 });
          return;
        }
        presentAutomaticGroups.add(group);
      }
      occupiedGroups.add(group);
      if (Number(item.quantity) !== 1)
        changes.push({ key: item.key, quantity: 1 });
    });

    desiredAutomaticByGroup.forEach((gift, group) => {
      if (!occupiedGroups.has(group) && !presentAutomaticGroups.has(group))
        additions.push(gift);
    });
    return { additions, changes };
  }

  function ajaxCartUrl(url) {
    const value = text(url);
    if (!value) return "";
    const queryIndex = value.indexOf("?");
    const path = queryIndex === -1 ? value : value.slice(0, queryIndex);
    const query = queryIndex === -1 ? "" : value.slice(queryIndex);
    return `${path.endsWith(".js") ? path : `${path}.js`}${query}`;
  }

  function quantityUpdates(data, line, quantity) {
    const items = Array.isArray(data?.items) ? data.items : [];
    const item = items[Number(line) - 1];
    const nextQuantity = Number(quantity);
    if (!item?.key || item.role === ROLE_BONUS ||
        !Number.isSafeInteger(nextQuantity) || nextQuantity < 0 ||
        nextQuantity >= Number(item.quantity)) return null;

    // Remove invalid rewards in the same request as the qualifying quantity.
    // Otherwise Shopify rejects the intermediate cart before we can reconcile it.
    const projected = {
      ...data,
      items: items.map((candidate) => candidate === item ? {
        ...candidate,
        quantity: nextQuantity,
        lineTotalMinor: Math.floor(Number(candidate.lineTotalMinor || 0) *
          nextQuantity / Number(candidate.quantity)),
      } : candidate),
    };
    const changes = desiredCartChanges(projected, buildCampaignState(projected)).changes;
    if (!changes.length) return null;
    return Object.fromEntries([
      [item.key, nextQuantity],
      ...changes.map((change) => [change.key, change.quantity]),
    ]);
  }

  function quantitiesUpdates(data, requested) {
    const updates = {};
    const projected = { ...data, items: (data.items || []).map((item) => {
      const quantity = Number(requested[item.key]);
      if (item.role === ROLE_BONUS || !Object.hasOwn(requested, item.key) ||
          !Number.isSafeInteger(quantity) || quantity < 0) return item;
      updates[item.key] = quantity;
      return { ...item, quantity, lineTotalMinor: Math.floor(
        Number(item.lineTotalMinor || 0) * quantity / Number(item.quantity || 1),
      ) };
    }) };
    if (!Object.keys(updates).length) return updates;
    for (const change of desiredCartChanges(projected, buildCampaignState(projected)).changes)
      updates[change.key] = change.quantity;
    return updates;
  }

  function parseCartResponse(raw, status, contentType) {
    const body = text(raw);
    if ((Number(status) === 429 || Number(status) === 430) &&
        /Verifying your connection|challenge-platform|challenges\.cloudflare/i.test(body))
      throw new Error("接続の確認が必要なため、カートを更新できませんでした。ページを再読み込みしてからお試しください。");
    try {
      return body ? JSON.parse(body) : {};
    } catch (_error) {
      if (Number(status) === 429)
        throw new Error("通信が混み合っています。少し待ってから再度お試しください。");
      if (
        text(contentType).includes("text/html") ||
        body.startsWith("<!DOCTYPE") ||
        body.startsWith("<html")
      ) {
        throw new Error("特典を更新できませんでした。ページを再読み込みしてお試しください。");
      }
      throw new Error("特典を更新できませんでした。");
    }
  }

  function cartErrorMessage(payload, status) {
    const detail =
      text(payload && payload.description) || text(payload && payload.message);
    if (
      Number(status) === 422 &&
      /(?:在庫|inventory|out of stock|sold out|not enough|can't add more)/i.test(
        detail,
      )
    )
      return "特典の在庫が不足しています。別の特典を選んでください。";
    return (
      detail ||
      (Number(status) === 429
        ? "通信が混み合っています。少し待ってから再度お試しください。"
        : "特典を更新できませんでした。")
    );
  }

  async function cartRequest(url, body, bonusRequest = true) {
    const endpoint = ajaxCartUrl(url);
    if (!endpoint) throw new Error("特典を更新できませんでした。");
    const response = await fetch(endpoint, {
      body: JSON.stringify({
        ...body,
        sections: CART_SECTIONS,
        sections_url: window.location.pathname,
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      method: "POST",
    });
    return readCartResponse(response, bonusRequest);
  }

  async function readCartResponse(response, bonusRequest = false) {
    const raw = await response.text();
    const payload = parseCartResponse(
      raw,
      response.status,
      response.headers.get("content-type"),
    );
    if (!response.ok || payload.status || payload.errors)
      throw new Error(bonusRequest ? cartErrorMessage(payload, response.status) :
        text(payload.description) || text(payload.errors) || text(payload.message) || "カートを更新できませんでした。もう一度お試しください。");
    return payload;
  }

  function replaceSection(documentRef, selector, html) {
    if (!html) return;
    const current = documentRef.querySelector(selector);
    const parsed = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    if (!current || !parsed) return;
    if (selector === "cart-drawer") {
      // Product forms retain this custom element, so keep its identity when a
      // reward refreshes the cart. Reconnect controls inside the new contents.
      current.innerHTML = parsed.innerHTML;
      current.classList.toggle("is-empty", parsed.classList.contains("is-empty"));
      current.setHeaderCartIconAccessibility?.();
      current.querySelector("#CartDrawer-Overlay")?.addEventListener("click", () => current.close());
      if (current.classList.contains("active") && typeof trapFocus === "function")
        trapFocus(current, current.querySelector(".drawer__close"));
      return;
    }
    current.replaceWith(documentRef.importNode(parsed, true));
  }

  function applySections(documentRef, payload) {
    const requiredSections = [
      ["#main-cart-items", "main-cart-items"],
      ["cart-drawer", "cart-drawer"],
    ].filter(([selector]) => documentRef.querySelector(selector));
    for (const [selector, section] of requiredSections) {
      const parsed = new DOMParser().parseFromString(
        payload?.sections?.[section] || "", "text/html",
      );
      const node = parsed.querySelector(`${selector} [data-astalie-bonus-cart]`);
      let data;
      try { data = JSON.parse(node?.textContent || "null"); } catch (_error) {}
      if (!Array.isArray(data?.items))
        throw new Error("カートの更新を確認できませんでした。ページを再読み込みしてお試しください。");
    }
    const drawerWasOpen = documentRef
      .querySelector("cart-drawer")
      ?.classList.contains("active");
    replaceSection(
      documentRef,
      "#cart-icon-bubble",
      payload.sections["cart-icon-bubble"],
    );
    replaceSection(
      documentRef,
      "#main-cart-items",
      payload.sections["main-cart-items"],
    );
    replaceSection(
      documentRef,
      "#main-cart-footer",
      payload.sections["main-cart-footer"],
    );
    replaceSection(documentRef, "cart-drawer", payload.sections["cart-drawer"]);
    if (drawerWasOpen)
      documentRef
        .querySelector("cart-drawer")
        ?.classList.add("active", "animate");
    cartStateUnconfirmed = false;
  }

  function bonusProperties(gift) {
    return {
      _astalie_bonus_auto: gift.grantMode,
      _astalie_bonus_campaign: gift.campaignId,
      _astalie_bonus_tier: String(gift.tier),
    };
  }

  function clearBonusError(documentRef) {
    documentRef
      .querySelectorAll("[data-astalie-bonus-error]")
      .forEach((node) => {
        node.hidden = true;
        node.textContent = "";
      });
  }

  function showBonusError(documentRef, error) {
    const message =
      error instanceof Error ? error.message : "特典を更新できませんでした。";
    documentRef
      .querySelectorAll("[data-astalie-bonus-error]")
      .forEach((node) => {
        node.hidden = false;
        node.textContent = message;
      });
    const modalError = documentRef.querySelector(
      "[data-astalie-bonus-modal-error]",
    );
    if (modalError) {
      modalError.hidden = false;
      modalError.textContent = message;
    }
  }

  async function applyCartPlan(documentRef, plan) {
    if (
      (typeof window !== "undefined" && window.storefrontSalesPaused) ||
      mutationRunning ||
      cartStateUnconfirmed ||
      (!plan.changes.length && !plan.additions.length)
    )
      return true;
    mutationRunning = true;
    setCheckoutPending(documentRef, 1);
    clearBonusError(documentRef);
    try {
      return await runCartMutation(async () => {
      // The cart may have changed while a customer's quantity batch was queued.
      const current = readCartData(documentRef);
      plan = desiredCartChanges(current, buildCampaignState(current));
      if (plan.changes.length) {
        const updates = {};
        plan.changes.forEach((change) => {
          updates[change.key] = change.quantity;
        });
        const payload = await cartRequest(window.routes.cart_update_url, {
          updates,
        });
        applySections(documentRef, payload);
      }
      if (plan.additions.length) {
        const payload = await cartRequest(window.routes.cart_add_url, {
          items: plan.additions.map((gift) => ({
            id: variantNumericId(gift.giftVariantId),
            properties: bonusProperties(gift),
            quantity: 1,
          })),
        });
        applySections(documentRef, payload);
      }
      return true;
      });
    } catch (error) {
      cartStateUnconfirmed = true;
      showBonusError(documentRef, new Error(
        `${error instanceof Error ? error.message.replace("別の特典を選んでください。", "") : "特典を更新できませんでした。"} 特典の追加が完了するまで購入手続きに進めません。ページを再読み込みしてお試しください。`,
      ));
      return false;
    } finally {
      mutationRunning = false;
      updateCheckoutPending(documentRef);
    }
  }

  function ensureChoiceModal(documentRef) {
    let modal = documentRef.querySelector("[data-astalie-bonus-modal]");
    if (modal) return modal;
    const holder = documentRef.createElement("div");
    holder.innerHTML = `
      <div class="astalie-bonus-modal" data-astalie-bonus-modal hidden>
        <div class="astalie-bonus-modal__backdrop" data-astalie-bonus-modal-close></div>
        <section class="astalie-bonus-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="AstalieBonusModalTitle" tabindex="-1">
          <header class="astalie-bonus-modal__header">
            <h2 id="AstalieBonusModalTitle" class="astalie-bonus-modal__title" data-astalie-bonus-modal-title></h2>
            <button class="astalie-bonus-modal__close" type="button" data-astalie-bonus-modal-close aria-label="閉じる"><span aria-hidden="true">&times;</span></button>
          </header>
          <div class="astalie-bonus-modal__body">
            <div class="astalie-bonus-modal__gallery">
              <div class="astalie-bonus-modal__media" data-astalie-bonus-modal-media></div>
              <div class="astalie-bonus-modal__thumbnails" data-astalie-bonus-modal-thumbnails></div>
            </div>
            <div class="astalie-bonus-modal__details">
              <div class="astalie-bonus-modal__options" role="listbox" aria-label="特典候補" data-astalie-bonus-modal-options></div>
              <h3 class="astalie-bonus-modal__product-title" data-astalie-bonus-modal-product-title></h3>
              <button class="button button--primary astalie-bonus-modal__confirm" type="button" data-astalie-bonus-modal-confirm>この特典を選ぶ</button>
              <p class="astalie-bonus__error" data-astalie-bonus-modal-error role="alert" hidden></p>
            </div>
          </div>
        </section>
      </div>`;
    modal = holder.firstElementChild;
    documentRef.body.append(modal);
    modal.addEventListener("click", (event) => {
      const close = event.target.closest("[data-astalie-bonus-modal-close]");
      if (close) {
        closeChoiceModal(documentRef);
        return;
      }
      const option = event.target.closest("[data-astalie-bonus-option]");
      if (option) {
        selectModalGift(modal, option.dataset.variantId);
        return;
      }
      const thumbnail = event.target.closest("[data-astalie-bonus-thumbnail]");
      if (thumbnail) {
        setModalImage(modal, Number(thumbnail.dataset.imageIndex));
        return;
      }
      if (event.target.closest("[data-astalie-bonus-modal-confirm]"))
        void confirmModalChoice(documentRef, modal);
    });
    modal.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") closeChoiceModal(documentRef);
    });
    return modal;
  }

  function setModalImage(modal, imageIndex) {
    const state = modal.astalieBonusState;
    const media = modal.querySelector("[data-astalie-bonus-modal-media]");
    const thumbnails = modal.querySelector(
      "[data-astalie-bonus-modal-thumbnails]",
    );
    if (!state || !media || !thumbnails) return;
    const images = state.activeGift.giftImages || [];
    const activeIndex = Math.max(
      0,
      Math.min(Number.isInteger(imageIndex) ? imageIndex : 0, images.length - 1),
    );
    media.replaceChildren();
    thumbnails.replaceChildren();
    if (!images.length) {
      media.append(
        createElement(
          modal.ownerDocument,
          "span",
          "astalie-bonus-modal__empty",
          "画像なし",
        ),
      );
      return;
    }
    const activeImage = images[activeIndex];
    const image = createElement(
      modal.ownerDocument,
      "img",
      "astalie-bonus-modal__main-image",
    );
    image.src = activeImage.src;
    image.alt = activeImage.alt || state.activeGift.giftTitle || "特典";
    if (activeImage.width) image.width = activeImage.width;
    if (activeImage.height) image.height = activeImage.height;
    media.append(image);
    if (images.length < 2) return;
    images.forEach((candidate, index) => {
      const button = createElement(
        modal.ownerDocument,
        "button",
        `astalie-bonus-modal__thumbnail${index === activeIndex ? " is-active" : ""}`,
      );
      button.type = "button";
      button.dataset.astalieBonusThumbnail = "true";
      button.dataset.imageIndex = String(index);
      button.setAttribute("aria-label", `${index + 1}枚目の画像`);
      button.setAttribute("aria-pressed", String(index === activeIndex));
      const thumb = createElement(modal.ownerDocument, "img");
      thumb.src = candidate.src;
      thumb.alt = "";
      thumb.loading = "lazy";
      button.append(thumb);
      thumbnails.append(button);
    });
  }

  function selectModalGift(modal, variantId) {
    const state = modal.astalieBonusState;
    if (!state) return;
    const gift = state.gifts.find(
      (candidate) => candidate.giftVariantId === variantId,
    );
    if (!gift) return;
    state.activeGift = gift;
    modal
      .querySelectorAll("[data-astalie-bonus-option]")
      .forEach((option) => {
        const selected = option.dataset.variantId === gift.giftVariantId;
        option.classList.toggle("is-active", selected);
        option.setAttribute("aria-selected", String(selected));
      });
    const title = modal.querySelector(
      "[data-astalie-bonus-modal-product-title]",
    );
    if (title) title.textContent = gift.giftTitle || `特典 ${gift.tier}`;
    const confirm = modal.querySelector("[data-astalie-bonus-modal-confirm]");
    if (confirm) {
      const unchanged = state.selectedVariantId === gift.giftVariantId;
      const unavailable = gift.giftAvailable === false;
      confirm.disabled = unchanged || unavailable;
      confirm.textContent = unchanged
        ? "選択済み"
        : unavailable
          ? "在庫不足"
          : "この特典を選ぶ";
    }
    setModalImage(modal, 0);
  }

  function renderModalOptions(modal) {
    const state = modal.astalieBonusState;
    const options = modal.querySelector("[data-astalie-bonus-modal-options]");
    if (!state || !options) return;
    options.replaceChildren();
    state.gifts.forEach((gift) => {
      const button = createElement(
        modal.ownerDocument,
        "button",
        "astalie-bonus-modal__option",
      );
      button.type = "button";
      button.dataset.astalieBonusOption = "true";
      button.dataset.variantId = gift.giftVariantId;
      button.setAttribute("role", "option");
      button.setAttribute("aria-selected", "false");
      button.disabled = gift.giftAvailable === false;
      const media = createElement(
        modal.ownerDocument,
        "span",
        "astalie-bonus-modal__option-media",
      );
      appendGiftImage(
        modal.ownerDocument,
        media,
        gift,
        "astalie-bonus-modal__option-image",
      );
      const copy = createElement(
        modal.ownerDocument,
        "span",
        "astalie-bonus-modal__option-copy",
      );
      copy.append(
        createElement(
          modal.ownerDocument,
          "span",
          "astalie-bonus-modal__option-title",
          gift.giftTitle || `特典 ${gift.tier}`,
        ),
      );
      if (gift.giftAvailable === false)
        copy.append(
          createElement(
            modal.ownerDocument,
            "span",
            "astalie-bonus-modal__option-stock",
            "在庫不足",
          ),
        );
      button.append(media, copy);
      options.append(button);
    });
  }

  function openChoiceModal(documentRef, opener) {
    const data = readCartData(documentRef);
    const campaigns = buildCampaignState(data);
    const campaignId = text(opener.dataset.campaignId);
    const tier = positiveInteger(opener.dataset.tier);
    const stage = campaigns
      .flatMap((campaign) => campaign.stages)
      .find(
        (candidate) =>
          candidate.campaignId === campaignId && candidate.tier === tier,
      );
    if (!stage || !stage.eligible) return;
    const gifts = stage.gifts.filter(
      (gift) => gift.grantMode === "choice" && gift.eligible,
    );
    if (!gifts.length) return;
    const selection = selectedChoiceForStage(data, stage);
    const modal = ensureChoiceModal(documentRef);
    modal.astalieBonusState = {
      activeGift:
        selection?.gift ||
        gifts.find((gift) => gift.giftAvailable === true) ||
        gifts[0],
      gifts,
      opener,
      selectedVariantId: selection?.gift.giftVariantId || "",
      stage,
    };
    const title = modal.querySelector("[data-astalie-bonus-modal-title]");
    if (title) title.textContent = `特典 ${tier}を選ぶ`;
    const error = modal.querySelector("[data-astalie-bonus-modal-error]");
    if (error) {
      error.hidden = true;
      error.textContent = "";
    }
    renderModalOptions(modal);
    selectModalGift(modal, modal.astalieBonusState.activeGift.giftVariantId);
    modal.hidden = false;
    modal.classList.add("is-open");
    documentRef.body.classList.add("overflow-hidden");
    const dialog = modal.querySelector("[role='dialog']");
    if (typeof trapFocus === "function") trapFocus(modal, dialog);
    else dialog?.focus();
  }

  function closeChoiceModal(documentRef) {
    const modal = documentRef.querySelector("[data-astalie-bonus-modal]");
    if (!modal || modal.hidden) return;
    const opener = modal.astalieBonusState?.opener;
    modal.hidden = true;
    modal.classList.remove("is-open");
    modal.astalieBonusState = null;
    if (typeof removeTrapFocus === "function") removeTrapFocus(opener);
    const drawer = documentRef.querySelector("cart-drawer.active");
    if (drawer && typeof trapFocus === "function") {
      const focusTarget = drawer.contains(opener)
        ? opener
        : drawer.querySelector("button, a, input");
      trapFocus(drawer, focusTarget || drawer);
    } else if (!documentRef.querySelector("modal-dialog[open], quick-add-modal[open]")) {
      documentRef.body.classList.remove("overflow-hidden");
    }
  }

  async function chooseGift(documentRef, requestedGift) {
    return runCartMutation(() => chooseGiftNow(documentRef, requestedGift));
  }

  async function chooseGiftNow(documentRef, requestedGift) {
    if (
      (typeof window !== "undefined" && window.storefrontSalesPaused) ||
      mutationRunning
    )
      return false;
    const data = readCartData(documentRef);
    const campaigns = buildCampaignState(data);
    const stage = campaigns
      .flatMap((campaign) => campaign.stages)
      .find(
        (candidate) =>
          candidate.campaignId === requestedGift.campaignId &&
          candidate.tier === requestedGift.tier,
      );
    const gift = stage?.gifts.find(
      (candidate) =>
        candidate.grantMode === "choice" &&
        candidate.eligible &&
        candidate.giftVariantId === requestedGift.giftVariantId,
    );
    const numericId = variantNumericId(gift?.giftVariantId);
    if (!stage?.eligible || !gift || !/^\d+$/.test(numericId)) return false;

    const existing = (data.items || []).filter((item) => {
      if (item.role !== ROLE_BONUS) return false;
      const config = normalizeConfig(item.config);
      return (
        config &&
        config.campaignId === stage.campaignId &&
        config.tier === stage.tier
      );
    });
    if (
      existing.length === 1 &&
      text(existing[0].variantId) === gift.giftVariantId
    ) {
      if (cartStateUnconfirmed) {
        showBonusError(documentRef, new Error("カートの更新を確認できませんでした。ページを再読み込みしてお試しください。"));
        return false;
      }
      return true;
    }

    mutationRunning = true;
    setCheckoutPending(documentRef, 1);
    clearBonusError(documentRef);
    try {
      let payload;
      if (existing.length) {
        const updates = { [numericId]: 1 };
        existing.forEach((item) => {
          updates[item.key] = 0;
        });
        payload = await cartRequest(window.routes.cart_update_url, { updates });
      } else {
        payload = await cartRequest(window.routes.cart_add_url, {
          items: [
            {
              id: numericId,
              properties: bonusProperties(gift),
              quantity: 1,
            },
          ],
        });
      }
      applySections(documentRef, payload);
      return true;
    } catch (error) {
      cartStateUnconfirmed = true;
      showBonusError(documentRef, error);
      return false;
    } finally {
      mutationRunning = false;
      updateCheckoutPending(documentRef);
    }
  }

  async function confirmModalChoice(documentRef, modal) {
    const state = modal.astalieBonusState;
    const confirm = modal.querySelector("[data-astalie-bonus-modal-confirm]");
    if (!state || !state.activeGift || !confirm || confirm.disabled) return;
    confirm.disabled = true;
    confirm.classList.add("is-loading");
    confirm.textContent = "追加中";
    const success = await chooseGift(documentRef, state.activeGift);
    confirm.classList.remove("is-loading");
    if (success) closeChoiceModal(documentRef);
    else {
      confirm.disabled = false;
      confirm.textContent = "この特典を選ぶ";
    }
  }

  function reconcile(documentRef) {
    const data = readCartData(documentRef);
    const campaigns = buildCampaignState(data);
    renderProgress(documentRef, data, campaigns);
    void applyCartPlan(documentRef, desiredCartChanges(data, campaigns));
  }

  function schedule(documentRef, delay) {
    updateCheckoutPending(documentRef);
    window.clearTimeout(scheduled);
    scheduled = window.setTimeout(() => reconcile(documentRef), delay || 0);
  }

  function mount(documentRef) {
    if (typeof window !== "undefined" && window.storefrontSalesPaused) return;
    const start = () => {
      schedule(documentRef, 0);
      const guardCheckout = (event) => {
        const checkout = event.type === "submit"
          ? event.submitter?.name === "checkout" ||
            ["cart", "CartDrawer-Form"].includes(event.target.id)
          : event.target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"]');
        if (!checkout || !updateCheckoutPending(documentRef)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        showBonusError(documentRef, new Error("特典の追加が完了するまで購入手続きに進めません。"));
      };
      documentRef.addEventListener("click", guardCheckout, true);
      documentRef.addEventListener("submit", guardCheckout, true);
      documentRef.addEventListener("click", (event) => {
        const opener = event.target.closest("[data-astalie-bonus-open]");
        if (!opener) return;
        event.preventDefault();
        openChoiceModal(documentRef, opener);
      });
      if (
        typeof subscribe === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          // CartItems publishes after replacing its cart sections.
          if (event?.source === "cart-items" && !mutationRunning)
            cartStateUnconfirmed = false;
          schedule(documentRef, 100);
        });
      }
      const observer = new MutationObserver((mutations) => {
        const hasCartData = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              (node.matches?.("[data-astalie-bonus-cart]") ||
                node.querySelector?.("[data-astalie-bonus-cart]")),
          ),
        );
        if (hasCartData) schedule(documentRef, 40);
      });
      observer.observe(documentRef.body, { childList: true, subtree: true });
    };
    if (documentRef.readyState === "loading")
      documentRef.addEventListener("DOMContentLoaded", start, { once: true });
    else start();
  }

  return {
    ajaxCartUrl,
    buildCampaignState,
    cartErrorMessage,
    cartRequest,
    checkoutPendingCount,
    conditionText,
    desiredCartChanges,
    eligibleChoiceStages,
    formatJpyMinor,
    mount,
    normalizeConfig,
    parseCartResponse,
    pendingChoiceStages,
    quantityUpdates,
    quantitiesUpdates,
    readCartData,
    readCartResponse,
    runCartMutation,
    applySections,
    selectedChoiceForStage,
    truncateStagePercent,
    variantNumericId,
  };
});
